import {
  frontendRoutes,
  preloadLikelyRoutePaths,
} from "@/config/frontendRoutes";

const routeImporters = {
  tutors: () => import("@/pages/tuteurs"),
  tutorsBySlug: () => import("@/pages/tuteurs-slug"),
  tutorsBySubjectCity: () => import("@/pages/tuteurs-matiere-ville"),
  tutorDetail: () => import("@/pages/tuteur"),
  connexion: () => import("@/pages/connexion"),
  inscription: () => import("@/pages/inscription"),
  verificationEmail: () => import("@/pages/verification-email"),
  forgotPassword: () => import("@/pages/forgot-password"),
  resetPassword: () => import("@/pages/reset-password"),
  dashboard: () => import("@/pages/tableau-de-bord"),
  listingEditor: () => import("@/pages/listing-editor"),
  onboarding: () => import("@/pages/onboarding"),
  contact: () => import("@/pages/contact"),
  demande: () => import("@/pages/demande"),
  publicProfile: () => import("@/pages/profil-public"),
  deconnexion: () => import("@/pages/deconnexion"),
  admin: () => import("@/pages/admin"),
  blog: () => import("@/pages/blog"),
  blogPost: () => import("@/pages/blog-post"),
  pressReleases: () => import("@/pages/press-releases"),
  pressReleasePost: () => import("@/pages/press-release-post"),
  mentionsLegales: () => import("@/pages/mentions-legales"),
  cgu: () => import("@/pages/cgu"),
  devenirProfesseur: () => import("@/pages/devenir-professeur"),
  devenirProfesseurFeature: () =>
    import("@/pages/devenir-professeur-fonctionnalite"),
  discoverProfLinker: () => import("@/pages/decouvrir-proflinker"),
  profLinker: () => import("@/pages/proflinker"),
  courseSubject: () => import("@/pages/cours-particuliers-subject"),
} as const;

const prefetched = new Set<string>();
const inFlightPrefetches = new Map<string, Promise<void>>();
const privateRoutePrefixes = ["/tableau-de-bord", "/admin", "/onboarding"];

const runPrefetch = (key: keyof typeof routeImporters) => {
  if (prefetched.has(key)) return;
  const existing = inFlightPrefetches.get(key);

  if (existing) return existing;
  const task = routeImporters[key]()
    .then(() => {
      prefetched.add(key);
    })
    .catch(() => {
      prefetched.delete(key);
    })
    .finally(() => {
      inFlightPrefetches.delete(key);
    });

  inFlightPrefetches.set(key, task);

  return task;
};

export const lazyRouteImporters = routeImporters;

type PrefetchMode = "idle" | "interaction" | "navigation";

type PreloadLikelyRoutesOptions = {
  isAuthenticated?: boolean;
};

type PreloadRouteByPathOptions = {
  mode?: PrefetchMode;
  isAuthenticated?: boolean;
};

const isPrivatePath = (path: string) =>
  privateRoutePrefixes.some((prefix) => path.startsWith(prefix));

export const preloadLikelyRoutes = ({
  isAuthenticated = false,
}: PreloadLikelyRoutesOptions = {}) => {
  preloadLikelyRoutePaths.forEach((path) =>
    preloadRouteByPath(path, { mode: "idle", isAuthenticated }),
  );
};

export const preloadRouteByPath = (
  pathValue: string,
  {
    mode = "interaction",
    isAuthenticated = false,
  }: PreloadRouteByPathOptions = {},
) => {
  const path = pathValue.split("?")[0].split("#")[0];

  if (!path) return Promise.resolve();
  const canPrefetchPrivate = mode !== "idle" || isAuthenticated;

  if (!canPrefetchPrivate && isPrivatePath(path)) {
    return Promise.resolve();
  }

  if (path === frontendRoutes.tutors) return runPrefetch("tutors");
  if (path.startsWith("/cours-particuliers"))
    return runPrefetch("courseSubject");
  if (path.startsWith("/cours/")) {
    const segments = path.split("/").filter(Boolean);

    if (segments.length >= 3) return runPrefetch("tutorDetail");
    if (segments.length === 2) return runPrefetch("tutorsBySlug");

    return runPrefetch("tutorsBySubjectCity");
  }
  if (path === frontendRoutes.connexion) return runPrefetch("connexion");
  if (path === frontendRoutes.inscription) return runPrefetch("inscription");
  if (path === frontendRoutes.verificationEmail)
    return runPrefetch("verificationEmail");
  if (path === frontendRoutes.forgotPassword)
    return runPrefetch("forgotPassword");
  if (path === frontendRoutes.resetPassword)
    return runPrefetch("resetPassword");
  if (path === frontendRoutes.dashboard) {
    if (!canPrefetchPrivate) return Promise.resolve();

    return runPrefetch("dashboard");
  }
  if (path === frontendRoutes.dashboardListingNew)
    return canPrefetchPrivate
      ? runPrefetch("listingEditor")
      : Promise.resolve();
  if (path.startsWith("/tableau-de-bord/annonces/"))
    return canPrefetchPrivate
      ? runPrefetch("listingEditor")
      : Promise.resolve();
  if (path === frontendRoutes.onboarding)
    return canPrefetchPrivate ? runPrefetch("onboarding") : Promise.resolve();
  if (path.startsWith("/profil/")) return runPrefetch("publicProfile");
  if (path === frontendRoutes.deconnexion) return runPrefetch("deconnexion");
  if (path === frontendRoutes.admin)
    return canPrefetchPrivate ? runPrefetch("admin") : Promise.resolve();
  if (path === frontendRoutes.blog) return runPrefetch("blog");
  if (path.startsWith("/blog/")) return runPrefetch("blogPost");
  if (path === frontendRoutes.pressReleases)
    return runPrefetch("pressReleases");
  if (path.startsWith("/press-releases/"))
    return runPrefetch("pressReleasePost");
  if (path === frontendRoutes.mentionsLegales)
    return runPrefetch("mentionsLegales");
  if (path === frontendRoutes.cgu) return runPrefetch("cgu");
  if (path === frontendRoutes.devenirProfesseur)
    return runPrefetch("devenirProfesseur");
  if (path.startsWith("/devenir-professeur/fonctionnalites/"))
    return runPrefetch("devenirProfesseurFeature");
  if (path === frontendRoutes.discoverProfLinker)
    return runPrefetch("discoverProfLinker");
  if (path === frontendRoutes.profLinker) return runPrefetch("profLinker");
  if (path === frontendRoutes.contact) return runPrefetch("contact");
  if (path === frontendRoutes.demande) return runPrefetch("demande");

  return Promise.resolve();
};
